// Buyer Handover Page — public, token-based
// Allows buyer's agent to download horse media/vet docs and sign off on receipt
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Download, CheckCircle2, FileText, Image, Video, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = window.location.origin;

const typeIcon = { image: Image, video: Video, vet_report: FileText, scope: FileText, xrays: FileText };
const typeLabel = { image: 'Photo', video: 'Video', vet_report: 'Vet Report', scope: 'Scope', xrays: 'X-Rays' };

export default function BuyerHandoverPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signOffForm, setSignOffForm] = useState({ name: '', email: '', notes: '', confirmed_papers: false, confirmed_horse: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/buyer-handover/${token}/data`)
      .then(r => { if (!r.ok) throw new Error('Link not found or expired'); return r.json(); })
      .then(d => { setData(d); if (d.signed_off) setSubmitted(true); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async (file) => {
    try {
      const normalizedUrl = file.file_url.startsWith('/') ? file.file_url.slice(1) : file.file_url;
      const res = await axios.get(`${API}/api/${normalizedUrl}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name || `${file.deliverable_type_horse}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleSignOff = async () => {
    if (!signOffForm.name.trim()) { toast.error('Please enter your name'); return; }
    if (!signOffForm.confirmed_papers || !signOffForm.confirmed_horse) {
      toast.error('Please confirm both receipt of papers and possession of the horse'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/buyer-handover/${token}/sign-off`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: signOffForm.name,
          email: signOffForm.email,
          notes: signOffForm.notes,
        }),
      });
      if (!res.ok) throw new Error('Sign-off failed');
      setSubmitted(true);
      toast.success('Sign-off recorded successfully');
      // Refresh data — best-effort; sign-off already recorded above
      const refreshRes = await fetch(`${API}/api/buyer-handover/${token}/data`);
      if (refreshRes.ok) setData(await refreshRes.json());
    } catch (e) {
      toast.error(e.message || 'Sign-off failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="pt-8 pb-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Link Not Found</h2>
          <p className="text-muted-foreground text-sm">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          {data?.tenant_logo && (
            <img src={data.tenant_logo} alt={data.tenant_name} className="h-12 mx-auto mb-3 object-contain" />
          )}
          <h1 className="text-2xl font-bold">{data?.tenant_name}</h1>
          <p className="text-muted-foreground">Horse Sale Handover</p>
        </div>

        {/* Horse info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Horse Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p><span className="font-medium">Horse:</span> {data?.horse_name || '—'}</p>
            <p><span className="font-medium">Hip #:</span> {data?.hip_number || '—'}</p>
            <p><span className="font-medium">Sale:</span> {data?.sale_name || '—'}</p>
          </CardContent>
        </Card>

        {/* Downloads */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Documents & Media</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.downloadable_files?.length > 0 ? (
              <div className="space-y-2">
                {data.downloadable_files.map(file => {
                  const Icon = typeIcon[file.deliverable_type_horse] || FileText;
                  return (
                    <div key={file.id} className="flex items-center justify-between p-3 border rounded hover:bg-muted/30">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{typeLabel[file.deliverable_type_horse] || file.deliverable_type_horse}</span>
                        {file.file_name && <span className="text-xs text-muted-foreground">({file.file_name})</span>}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleDownload(file)}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No documents available yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Sign-off */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Digital Sign-off
              {submitted && <Badge className="bg-green-600 text-white">Completed</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-4 space-y-2">
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                <p className="font-medium text-green-700">Sign-off recorded</p>
                <p className="text-sm text-muted-foreground">
                  Confirmed by <strong>{data?.signed_off_by_name}</strong>
                  {data?.signed_off_at && ` on ${new Date(data.signed_off_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
                </p>
                {data?.sign_off_notes && <p className="text-sm italic">"{data.sign_off_notes}"</p>}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Please confirm that you have received all physical paperwork and taken possession of <strong>{data?.horse_name}</strong>.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Your Name *</Label>
                    <Input value={signOffForm.name} onChange={e => setSignOffForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
                  </div>
                  <div className="space-y-1">
                    <Label>Your Email</Label>
                    <Input type="email" value={signOffForm.email} onChange={e => setSignOffForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="confirmed_papers"
                      checked={signOffForm.confirmed_papers}
                      onCheckedChange={v => setSignOffForm(f => ({ ...f, confirmed_papers: v }))}
                      className="mt-0.5"
                    />
                    <Label htmlFor="confirmed_papers" className="cursor-pointer font-normal">
                      I confirm that I have received all physical papers for <strong>{data?.horse_name}</strong>
                    </Label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="confirmed_horse"
                      checked={signOffForm.confirmed_horse}
                      onCheckedChange={v => setSignOffForm(f => ({ ...f, confirmed_horse: v }))}
                      className="mt-0.5"
                    />
                    <Label htmlFor="confirmed_horse" className="cursor-pointer font-normal">
                      I confirm that I have taken physical possession of <strong>{data?.horse_name}</strong>
                    </Label>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Notes (optional)</Label>
                  <Textarea value={signOffForm.notes} onChange={e => setSignOffForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any additional notes..." rows={2} />
                </div>
                <Button onClick={handleSignOff} disabled={submitting} className="w-full bg-primary hover:bg-primary/90 text-white">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & Sign Off</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
